import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Landmark, Receipt, Wallet, Search, Filter, Plus, X, CheckCircle2, Loader2,
  TrendingUp, ArrowRight, Ban, FileText, CalendarDays,
} from "lucide-react";

const PAYMENT_MODES = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash" },
];

const MODE_LABEL = Object.fromEntries(PAYMENT_MODES.map((m) => [m.value, m.label]));

const MILESTONE_LABEL = {
  booking: "Booking · 5%",
  design_closure: "Design Closure · 45%",
  carcass_completion: "Carcass Completion · 45%",
  final_settlement: "Final Settlement · 5%",
};

const inr = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="border border-[#262626] bg-[#0a0a0a] p-5 rounded-md" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">{label}</p>
      <p className="font-display text-2xl text-white mt-2">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function MilestoneChip({ m }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${
        m.cleared
          ? "text-[#d4af37] border-[#d4af37]/50 bg-[#d4af37]/5"
          : "text-gray-500 border-gray-700"
      }`}
      data-testid={`ms-chip-${m.key}`}
      title={
        m.stage_amount != null
          ? `Stage ${m.stage_percent}% · ₹${(m.stage_amount || 0).toLocaleString("en-IN")}`
          : undefined
      }
    >
      {m.cleared && <CheckCircle2 className="w-3 h-3" strokeWidth={2} />}
      {(m.label || m.key)} · {m.stage_percent ?? m.percent}%
    </span>
  );
}

function ModePill({ mode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border border-gray-700 text-gray-300">
      {MODE_LABEL[mode] || mode}
    </span>
  );
}

// ---------- Log Receipt Modal ----------
function LogReceiptModal({ open, onClose, preselectProject, ledger, onLogged }) {
  const [projectId, setProjectId] = useState(preselectProject || "");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("bank_transfer");
  const [txn, setTxn] = useState("");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setProjectId(preselectProject || "");
      setAmount("");
      setMode("bank_transfer");
      setTxn("");
      setReceiptDate(new Date().toISOString().slice(0, 10));
      setNotes("");
    }
  }, [open, preselectProject]);

  const selectedProject = useMemo(
    () => ledger.find((p) => p.project_id === projectId),
    [ledger, projectId]
  );

  if (!open) return null;

  const submit = async () => {
    if (!projectId) return toast.error("Select a project");
    const amt = parseFloat(amount);
    if (!(amt > 0)) return toast.error("Amount must be > 0");
    if (!txn.trim()) return toast.error("Transaction reference is required");
    setSaving(true);
    try {
      const { data } = await api.post("/finance/receipts", {
        project_id: projectId,
        amount: amt,
        mode,
        transaction_reference: txn.trim(),
        receipt_date: receiptDate,
        notes: notes.trim() || null,
      });
      const crossed = (data.milestones_crossed || []).map((k) => MILESTONE_LABEL[k]).join(", ");
      toast.success(
        crossed
          ? `Receipt ${data.receipt_number} logged · Crossed ${crossed}`
          : `Receipt ${data.receipt_number} logged`
      );
      onLogged?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="log-receipt-modal">
      <div className="w-full max-w-lg border border-[#262626] bg-[#0a0a0a] rounded-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Finance</p>
            <h2 className="font-display text-xl text-white">Log Receipt</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="log-receipt-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Project">
            <select
              className="iz-input w-full"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="log-receipt-project"
            >
              <option value="">— Select project —</option>
              {ledger.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_name} · {p.client_name} · Balance ₹{inr(p.balance_due)}
                </option>
              ))}
            </select>
          </Field>
          {selectedProject && (
            <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500 border border-[#262626] rounded px-3 py-2 bg-[#050505]">
              Final Payable ₹{inr(selectedProject.final_payable ?? selectedProject.project_amount)}
              {(selectedProject.discount || 0) > 0 && (
                <> · Discount ₹{inr(selectedProject.discount)}</>
              )}
              {" · Received "}₹{inr(selectedProject.amount_received)}
              {" · "}<span className="text-[#d4af37]">{selectedProject.completion_percent}%</span> cleared
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (₹)">
              <input
                type="number"
                min={1}
                step="0.01"
                className="iz-input w-full"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="log-receipt-amount"
              />
            </Field>
            <Field label="Mode">
              <select className="iz-input w-full" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="log-receipt-mode">
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Transaction Reference">
              <input
                className="iz-input w-full"
                value={txn}
                onChange={(e) => setTxn(e.target.value)}
                placeholder="e.g. UTR / cheque no."
                data-testid="log-receipt-txn"
              />
            </Field>
            <Field label="Receipt Date">
              <input
                type="date"
                className="iz-input w-full"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                data-testid="log-receipt-date"
              />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea
              rows={2}
              className="iz-input w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal remarks…"
              data-testid="log-receipt-notes"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#262626]">
          <button onClick={onClose} className="iz-btn-secondary text-xs" data-testid="log-receipt-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="iz-btn-primary flex items-center gap-2 text-xs"
            data-testid="log-receipt-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            Log Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ---------- Discount Modal ----------
function DiscountModal({ open, project, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && project) setValue(String(project.discount ?? 0));
  }, [open, project]);
  if (!open || !project) return null;
  const maxDiscount = Number(project.original_value ?? project.project_amount ?? 0);
  const disc = parseFloat(value);
  const newFinal = Math.max(0, maxDiscount - (isNaN(disc) ? 0 : disc));
  const submit = async () => {
    const d = parseFloat(value);
    if (isNaN(d) || d < 0) return toast.error("Discount must be ≥ 0");
    if (d > maxDiscount)
      return toast.error(`Discount cannot exceed contract value ₹${maxDiscount.toLocaleString("en-IN")}`);
    setSaving(true);
    try {
      await api.post(`/finance/projects/${project.id}/discount`, { discount: d });
      toast.success("Discount updated · milestones recalculated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="discount-modal">
      <div className="w-full max-w-md border border-[#d4af37]/40 bg-[#0a0a0a] rounded-md">
        <div className="px-5 py-4 border-b border-[#262626]">
          <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Finance · Adjustment</p>
          <h3 className="font-display text-lg text-white">Set Project Discount</h3>
          <p className="text-[11px] text-gray-500 mt-1">Fixed rupee amount deducted from the contract value. Milestones (5% · 45% · 45% · 5%) auto-recompute on the resulting Final Payable.</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
            Original Value · <span className="text-white">₹{maxDiscount.toLocaleString("en-IN")}</span>
          </div>
          <Field label="Discount (₹)">
            <input
              type="number"
              min={0}
              step="1"
              className="iz-input w-full"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="discount-input"
            />
          </Field>
          <div className="text-xs text-gray-400 font-mono border border-[#262626] rounded px-3 py-2 bg-[#050505]">
            Final Payable → <span className="text-[#d4af37]">₹{newFinal.toLocaleString("en-IN")}</span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#262626]">
          <button className="iz-btn-secondary text-xs" onClick={onClose} data-testid="discount-cancel">Cancel</button>
          <button className="iz-btn-primary text-xs flex items-center gap-2" onClick={submit} disabled={saving} data-testid="discount-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Save Discount
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Project Drill Drawer ----------
function ProjectDrillDrawer({ projectId, canLog, canVoid, canEditDiscount, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [voidId, setVoidId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setState({ loading: true, data: null, error: null });
    try {
      const { data } = await api.get(`/finance/projects/${projectId}/ledger`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: formatApiErrorDetail(e?.response?.data?.detail) });
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const voidReceipt = async () => {
    if (!voidReason.trim() || voidReason.trim().length < 3)
      return toast.error("Reason must be at least 3 characters");
    setVoiding(true);
    try {
      await api.post(`/finance/receipts/${voidId}/void`, { reason: voidReason.trim() });
      toast.success("Receipt voided");
      setVoidId(null);
      setVoidReason("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setVoiding(false);
    }
  };

  if (!projectId) return null;
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="project-drill-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-[#080808] border-l border-[#262626] overflow-y-auto">
        <div className="sticky top-0 bg-[#080808] border-b border-[#262626] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Project Ledger</p>
            <h2 className="font-display text-xl text-white">{state.data?.project?.client_name || "…"}{state.data?.project?.city ? ` · ${state.data?.project?.city}` : ""}</h2>
            <p className="text-xs text-gray-500">{state.data?.project?.project_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="drill-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {state.loading && (
          <div className="p-10 text-center text-gray-500 text-sm">Loading ledger…</div>
        )}
        {state.error && (
          <div className="p-10 text-center text-red-400 text-sm" data-testid="drill-error">{state.error}</div>
        )}

        {state.data && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <KpiCard label="Original Value" value={`₹${inr(state.data.project.original_value ?? state.data.project.project_amount)}`} testid="drill-original" />
              <KpiCard label="Discount"
                value={`₹${inr(state.data.project.discount || 0)}`}
                sub={canEditDiscount ? "Editable · Finance/Admin" : "Read-only"}
                testid="drill-discount" />
              <KpiCard label="Final Payable" value={`₹${inr(state.data.project.final_payable ?? state.data.project.project_amount)}`} testid="drill-final-payable" />
              <KpiCard label="Received" value={`₹${inr(state.data.project.amount_received)}`} testid="drill-received" />
              <KpiCard label="Balance" value={`₹${inr(state.data.project.balance_due)}`} testid="drill-balance" />
              {canEditDiscount && (
                <button
                  onClick={() => setDiscountOpen(true)}
                  className="iz-btn-secondary text-xs flex items-center justify-center gap-2 border-[#d4af37]/50 text-[#d4af37]"
                  data-testid="edit-discount-btn"
                >
                  <FileText className="w-3.5 h-3.5" /> Edit Discount
                </button>
              )}
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Milestones · 5 · 45 · 45 · 5</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {state.data.milestones.map((m) => (
                  <div key={m.key}
                       className={`border rounded p-3 flex items-center justify-between ${
                         m.cleared ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#262626] bg-[#050505]"
                       }`}
                       data-testid={`drill-ms-${m.key}`}>
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                        {m.label} · {m.stage_percent}%
                      </p>
                      <p className="text-sm text-white mt-1">₹{inr(m.stage_amount)}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Cumulative ₹{inr(m.threshold_amount)}</p>
                    </div>
                    {m.cleared ? (
                      <CheckCircle2 className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                        pending ₹{inr(m.remaining_to_clear)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Receipts ({state.data.receipts.length})</p>
              {state.data.receipts.length === 0 && (
                <p className="text-xs text-gray-600 italic">No receipts yet.</p>
              )}
              <div className="space-y-2">
                {state.data.receipts.slice().reverse().map((r) => (
                  <div key={r.id}
                       className={`border rounded p-3 ${r.voided ? "border-red-900 bg-red-950/10" : "border-[#262626] bg-[#050505]"}`}
                       data-testid={`drill-receipt-${r.receipt_number}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-[11px] text-[#d4af37]">{r.receipt_number}</p>
                        <p className="text-sm text-white">₹{inr(r.amount)} <span className="text-gray-500 text-xs">· {r.transaction_reference}</span></p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {r.receipt_date} · <ModePill mode={r.mode} />
                          {r.voided && <span className="ml-2 text-red-400">VOIDED</span>}
                        </p>
                        {r.notes && <p className="text-[11px] text-gray-400 mt-1 italic">{r.notes}</p>}
                        {r.milestones_crossed?.length > 0 && (
                          <p className="text-[10px] text-[#d4af37] mt-1">
                            ↑ Crossed: {r.milestones_crossed.map((k) => MILESTONE_LABEL[k]).join(", ")}
                          </p>
                        )}
                        {r.voided && r.void_reason && (
                          <p className="text-[10px] text-red-400 mt-1">Reason: {r.void_reason}</p>
                        )}
                      </div>
                      {!r.voided && canVoid && (
                        <button
                          onClick={() => { setVoidId(r.id); setVoidReason(""); }}
                          className="iz-btn-danger text-[10px] px-2 py-1 flex items-center gap-1"
                          data-testid={`void-btn-${r.receipt_number}`}
                        >
                          <Ban className="w-3 h-3" /> Void
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </aside>

      {voidId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
          <div className="w-full max-w-md border border-red-800 bg-[#0a0a0a] rounded-md">
            <div className="px-5 py-4 border-b border-red-800">
              <h3 className="font-display text-lg text-white">Void Receipt</h3>
              <p className="text-xs text-red-300 mt-1">This reverses the amount from the project ledger.</p>
            </div>
            <div className="p-5">
              <Field label="Reason (required)">
                <textarea
                  rows={3}
                  className="iz-input w-full"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  data-testid="void-reason-input"
                />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-red-800">
              <button className="iz-btn-secondary text-xs" onClick={() => setVoidId(null)}>Cancel</button>
              <button
                className="iz-btn-danger text-xs flex items-center gap-2"
                onClick={voidReceipt}
                disabled={voiding}
                data-testid="void-confirm"
              >
                {voiding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                Confirm Void
              </button>
            </div>
          </div>
        </div>
      )}

      <DiscountModal
        open={discountOpen}
        project={state.data?.project}
        onClose={() => setDiscountOpen(false)}
        onSaved={async () => { await load(); onChanged?.(); }}
      />
    </div>
  );
}

// ---------- Main Page ----------
export default function FinanceLedgerPage() {
  const { principal, roleName, isAdmin } = useAuth();
  const canLog = isAdmin || ["Finance Manager", "Finance Executive"].includes(roleName);
  const canVoid = isAdmin || roleName === "Finance Manager";
  const canEditDiscount = isAdmin || roleName === "Finance Manager";

  const [tab, setTab] = useState("ledger"); // ledger | receipts | reconciliation
  const [ledger, setLedger] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(true);

  const [drillProjectId, setDrillProjectId] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [logPreselect, setLogPreselect] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, r] = await Promise.all([
        api.get("/finance/ledger"),
        api.get("/finance/reconciliation"),
      ]);
      setLedger(l.data);
      setRecon(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReceipts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (modeFilter) params.set("mode", modeFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (includeVoided) params.set("include_voided", "true");
      if (search) params.set("q", search);
      const { data } = await api.get(`/finance/receipts?${params}`);
      setReceipts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [modeFilter, dateFrom, dateTo, includeVoided, search]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === "receipts") loadReceipts(); }, [tab, loadReceipts]);

  const filteredLedger = useMemo(() => {
    if (!search) return ledger;
    const s = search.toLowerCase();
    return ledger.filter(
      (p) =>
        (p.project_name || "").toLowerCase().includes(s) ||
        (p.client_name || "").toLowerCase().includes(s) ||
        (p.city || "").toLowerCase().includes(s)
    );
  }, [ledger, search]);

  const openLog = (projectId = "") => {
    setLogPreselect(projectId);
    setLogOpen(true);
  };

  const afterReceiptChange = async () => {
    await loadAll();
    if (tab === "receipts") await loadReceipts();
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-[#262626] px-8 py-6 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37]">Finance · Accounts</p>
          <h1 className="font-display text-3xl text-white mt-1">Payment Ledger</h1>
          <p className="text-xs text-gray-500 mt-1">Signed in as {principal?.name} · {roleName || (isAdmin ? "Admin" : "")}</p>
        </div>
        {canLog && (
          <button
            onClick={() => openLog("")}
            className="iz-btn-primary flex items-center gap-2 text-xs"
            data-testid="global-log-receipt"
          >
            <Plus className="w-4 h-4" /> Log Receipt
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="px-8 py-3 border-b border-[#262626] flex items-center gap-4">
        {[
          { k: "ledger", label: "Ledger", icon: Landmark },
          { k: "receipts", label: "Receipts", icon: Receipt },
          { k: "reconciliation", label: "Reconciliation", icon: TrendingUp },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${
                tab === t.k
                  ? "text-[#d4af37] border-[#d4af37]"
                  : "text-gray-500 border-transparent hover:text-gray-300"
              }`}
              data-testid={`tab-${t.k}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      <main className="px-8 py-6">
        {loading && <p className="text-gray-500 text-sm">Loading finance workspace…</p>}

        {!loading && tab === "ledger" && (
          <>
            <div className="mb-5 flex items-center gap-3">
              <div className="relative flex-1 max-w-lg">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-600" />
                <input
                  className="iz-input pl-9 w-full"
                  placeholder="Search project, client, city…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="ledger-search"
                />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                {filteredLedger.length} projects
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {filteredLedger.map((p) => (
                <div
                  key={p.project_id}
                  className="border border-[#262626] hover:border-[#d4af37]/60 rounded-md p-5 bg-[#0a0a0a] transition-colors"
                  data-testid={`ledger-row-${p.project_id}`}
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-lg text-white truncate">{p.client_name}{p.city ? ` · ${p.city}` : ""}</h3>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{p.status}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{p.project_name}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {p.milestones.map((m) => <MilestoneChip key={m.key} m={m} />)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Balance</p>
                      <p className="font-display text-2xl text-[#d4af37]" data-testid={`ledger-balance-${p.project_id}`}>₹{inr(p.balance_due)}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        ₹{inr(p.amount_received)} / ₹{inr(p.final_payable ?? p.project_amount)}
                      </p>
                      {(p.discount || 0) > 0 && (
                        <p className="text-[10px] text-gray-500" data-testid={`ledger-discount-${p.project_id}`}>
                          Original ₹{inr(p.original_value)} · Disc ₹{inr(p.discount)}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-600">{p.completion_percent}% cleared</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#1a1a1a] flex items-center justify-between">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600">
                      {p.receipts_count} receipt{p.receipts_count === 1 ? "" : "s"}
                      {p.next_milestone && <> · next: <span className="text-gray-400">{MILESTONE_LABEL[p.next_milestone]}</span></>}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setDrillProjectId(p.project_id)}
                        className="iz-btn-secondary text-[10px] px-2 py-1 flex items-center gap-1"
                        data-testid={`drill-${p.project_id}`}
                      >
                        <FileText className="w-3 h-3" /> Ledger
                      </button>
                      {canLog && (
                        <button
                          onClick={() => openLog(p.project_id)}
                          className="iz-btn-primary text-[10px] px-2 py-1 flex items-center gap-1"
                          data-testid={`log-${p.project_id}`}
                        >
                          <Receipt className="w-3 h-3" /> Log Receipt
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredLedger.length === 0 && (
                <p className="text-gray-600 italic text-sm">No projects match your search.</p>
              )}
            </div>
          </>
        )}

        {!loading && tab === "receipts" && (
          <>
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-600" />
                <input
                  className="iz-input pl-9 w-full"
                  placeholder="Search txn / project / client…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="receipts-search"
                />
              </div>
              <select className="iz-input" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} data-testid="receipts-mode-filter">
                <option value="">All modes</option>
                {PAYMENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-gray-500">
                <CalendarDays className="w-3 h-3" />
                <input type="date" className="iz-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="receipts-date-from" />
                <ArrowRight className="w-3 h-3" />
                <input type="date" className="iz-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="receipts-date-to" />
              </div>
              <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gray-400 cursor-pointer">
                <input type="checkbox" checked={includeVoided} onChange={(e) => setIncludeVoided(e.target.checked)} data-testid="receipts-include-voided" />
                Include voided
              </label>
              <button onClick={loadReceipts} className="iz-btn-secondary text-xs flex items-center gap-1" data-testid="receipts-apply">
                <Filter className="w-3 h-3" /> Apply
              </button>
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                {receipts.length} entries
              </span>
            </div>

            <div className="border border-[#262626] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Receipt #</th>
                    <th className="text-left px-4 py-3">Project</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Mode</th>
                    <th className="text-left px-4 py-3">Txn Ref</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Logged by</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setDrillProjectId(r.project_id)}
                      className={`border-t border-[#1a1a1a] cursor-pointer hover:bg-[#0f0f0f] ${r.voided ? "opacity-60" : ""}`}
                      data-testid={`receipt-row-${r.receipt_number}`}
                    >
                      <td className="px-4 py-3 font-mono text-[11px] text-[#d4af37]">{r.receipt_number}</td>
                      <td className="px-4 py-3 text-gray-200">{r.project_name}<div className="text-[10px] text-gray-500">{r.client_name}</div></td>
                      <td className="px-4 py-3 text-right text-white">₹{inr(r.amount)}</td>
                      <td className="px-4 py-3"><ModePill mode={r.mode} /></td>
                      <td className="px-4 py-3 font-mono text-[11px] text-gray-400">{r.transaction_reference}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{r.receipt_date}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{r.logged_by_name}</td>
                      <td className="px-4 py-3">
                        {r.voided ? (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-red-400">Voided</span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {receipts.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-600 italic text-sm">No receipts match the filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && tab === "reconciliation" && recon && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <KpiCard label="Original Contract" value={`₹${inr(recon.total_contract_value)}`} testid="kpi-contract" />
              <KpiCard label="Discounts" value={`₹${inr(recon.total_discount || 0)}`} testid="kpi-discount" />
              <KpiCard label="Final Payable" value={`₹${inr(recon.total_final_payable ?? recon.total_contract_value)}`} testid="kpi-final-payable" />
              <KpiCard label="Received to date" value={`₹${inr(recon.total_received)}`} sub={`${recon.collection_rate_percent}% collected`} testid="kpi-received" />
              <KpiCard label="Outstanding" value={`₹${inr(recon.total_pending)}`} testid="kpi-pending" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Projects by highest milestone cleared</p>
                <ul className="space-y-2">
                  {Object.entries(recon.projects_by_highest_cleared).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">
                        {k === "not_started" ? "Not started" : MILESTONE_LABEL[k]}
                      </span>
                      <span className="font-display text-lg text-[#d4af37]">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Receipts by mode · last 30 days</p>
                {recon.receipts_by_mode_30d.length === 0 && <p className="text-xs text-gray-600 italic">No receipts logged in the last 30 days.</p>}
                <ul className="space-y-2">
                  {recon.receipts_by_mode_30d.map((row) => (
                    <li key={row.mode} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300 flex items-center gap-2"><Wallet className="w-3.5 h-3.5 text-[#d4af37]" /> {MODE_LABEL[row.mode] || row.mode}</span>
                      <span className="text-white">₹{inr(row.total)} <span className="text-gray-500 text-[10px]">· {row.count}x</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>

      <LogReceiptModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        preselectProject={logPreselect}
        ledger={ledger}
        onLogged={afterReceiptChange}
      />
      <ProjectDrillDrawer
        projectId={drillProjectId}
        canLog={canLog}
        canVoid={canVoid}
        canEditDiscount={canEditDiscount}
        onClose={() => setDrillProjectId(null)}
        onChanged={afterReceiptChange}
      />
    </div>
  );
}
